import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TicketIcon, 
  ClipboardDocumentIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

const Coupon = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Admin check
  useEffect(() => {
    if (!user?.is_staff && !user?.is_superuser) {
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  // State
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [gameExtras, setGameExtras] = useState({ SKT: false, MIC: false, MEK: false, RMS: false, ROT: false, OWT: false, PPT: false });
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [generatedCoupon, setGeneratedCoupon] = useState(null);
  const [couponHistory, setCouponHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState('');
  
  // Fetch courses on mount
  useEffect(() => {
    fetchCourses();
    fetchCouponHistory();
  }, []);
  
  const fetchCourses = async () => {
    try {
      const data = await apiClient.get('/api/payments/store/');
      setCourses(data.courses || []);
    } catch (error) {
      // Failed to fetch courses
    }
  };
  
  const fetchCouponHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await apiClient.get('/api/payments/purchase-coupons/');
      setCouponHistory(data.coupons || []);
    } catch (error) {
      // Failed to fetch coupon history
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Handle course selection
  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setPricingTiers(course.pricing_tiers || []);
    setSelectedTier(null);
    setGameExtras({ SKT: false, MIC: false, MEK: false, RMS: false, ROT: false, OWT: false, PPT: false });
    setGeneratedCoupon(null);
  };
  
  // Calculate total price
  const calculateTotalPrice = () => {
    if (!selectedTier) return 0;
    let total = parseFloat(selectedTier.price);
    if (selectedCourse?.code === 'DLR') {
      if (gameExtras.SKT) total += 14;
      if (gameExtras.MIC) total += 14;
      if (gameExtras.OWT) total += 14;
      if (gameExtras.ROT) total += 14;
      if (gameExtras.MEK) total += 10;
      if (gameExtras.RMS) total += 10;
      if (gameExtras.PPT) total += 10;
    }
    return total;
  };
  
  // Generate coupon (admin)
  const handleGenerateCoupon = async () => {
    if (!selectedTier) return;
    
    setGenerating(true);
    setError('');
    try {
      const data = await apiClient.post('/api/payments/purchase-coupons/generate/', {
        pricing_tier_id: selectedTier.id,
        game_extras: selectedCourse?.code === 'DLR' ? gameExtras : {},
        expires_in_days: expiresInDays
      });
      
      if (data.success) {
        setGeneratedCoupon(data.coupon);
        fetchCouponHistory(); // Refresh history
      } else {
        setError(data.error || 'Failed to generate coupon');
      }
    } catch (error) {
      setError(error.message || 'Failed to generate coupon');
    } finally {
      setGenerating(false);
    }
  };
  
  // Copy coupon code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
  };
  

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <ShieldCheckIcon className="h-7 w-7 mr-2 text-primary-500" />
          Coupon Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Generate purchase coupons for customers. Coupons are redeemed in the Store page.
        </p>
      </div>
      
      <div className="space-y-6">
          {/* Generate Coupon Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Generate New Coupon
            </h2>
            
            {/* Course Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Course
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {courses.map(course => (
                  <button
                    key={course.id}
                    onClick={() => handleCourseSelect(course)}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      selectedCourse?.id === course.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-900 dark:text-white">{course.code}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{course.name}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Pricing Tier Selection */}
            {selectedCourse && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Duration
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {pricingTiers.map(tier => (
                    <button
                      key={tier.id}
                      onClick={() => { setSelectedTier(tier); setGeneratedCoupon(null); }}
                      className={`p-3 rounded-lg border-2 text-center transition-colors ${
                        selectedTier?.id === tier.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-semibold text-gray-900 dark:text-white">{tier.name}</p>
                      <p className="text-sm text-green-600">${tier.price}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Game Extras (DLR only) */}
            {selectedCourse?.code === 'DLR' && selectedTier && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Game Extras
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { code: 'SKT', name: 'SKT - Concentrativeness', price: 14 },
                    { code: 'MIC', name: 'MIC - Instrument Coordination', price: 14 },
                    { code: 'OWT', name: 'OWT - Perceptual Speed', price: 14 },
                    { code: 'ROT', name: 'ROT - Cube Rotation', price: 14 },
                    { code: 'MEK', name: 'MEK - Visual Memory', price: 10 },
                    { code: 'RMS', name: 'RMS - Running Memory Span', price: 10 },
                    { code: 'PPT', name: 'PPT - Paper Folding Test', price: 10 }
                  ].map(game => (
                    <label key={game.code} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={gameExtras[game.code]}
                        onChange={(e) => {
                          setGameExtras(prev => ({ ...prev, [game.code]: e.target.checked }));
                          setGeneratedCoupon(null);
                        }}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {game.name} (+${game.price})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Expiry */}
            {selectedTier && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Coupon Valid For (days)
                </label>
                <input
                  type="number"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                  min={1}
                  max={365}
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            )}
            
            {/* Total and Generate Button */}
            {selectedTier && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Value</p>
                  <p className="text-2xl font-bold text-green-600">${calculateTotalPrice().toFixed(2)}</p>
                </div>
                <button
                  onClick={handleGenerateCoupon}
                  disabled={generating}
                  className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                >
                  {generating ? 'Generating...' : 'Generate Coupon'}
                </button>
              </div>
            )}
            
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                {error}
              </div>
            )}
          </div>
          
          {/* Generated Coupon Display */}
          {generatedCoupon && (
            <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-green-700 dark:text-green-400 flex items-center">
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  Coupon Generated!
                </h3>
                <span className="text-sm text-green-600 dark:text-green-500">
                  Expires: {new Date(generatedCoupon.expires_at).toLocaleDateString()}
                </span>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Coupon Code</p>
                <div className="flex items-center gap-2">
                  <code className="text-2xl font-mono font-bold text-gray-900 dark:text-white tracking-wider">
                    {generatedCoupon.code}
                  </code>
                  <button
                    onClick={() => copyToClipboard(generatedCoupon.code)}
                    className="p-2 text-gray-500 hover:text-primary-600 transition-colors"
                    title="Copy to clipboard"
                  >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Course</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{generatedCoupon.course}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Duration</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{generatedCoupon.tier_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Value</p>
                  <p className="font-semibold text-green-600">${generatedCoupon.total_price.toFixed(2)}</p>
                </div>
                {Object.values(generatedCoupon.game_extras || {}).some(v => v) && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Game Access</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {Object.entries(generatedCoupon.game_extras).filter(([,v]) => v).map(([k]) => k).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Coupon History */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-gray-500" />
              Recent Coupons
            </h2>
            
            {loadingHistory ? (
              <div className="text-center py-4 text-gray-500">Loading...</div>
            ) : couponHistory.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No coupons generated yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Code</th>
                      <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Course</th>
                      <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Value</th>
                      <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                      <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Used By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponHistory.slice(0, 20).map(coupon => (
                      <tr key={coupon.id} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className="py-2 px-2">
                          <code className="font-mono text-gray-900 dark:text-white">{coupon.code}</code>
                        </td>
                        <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{coupon.course_code}</td>
                        <td className="py-2 px-2 text-green-600">${coupon.total_price.toFixed(2)}</td>
                        <td className="py-2 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            coupon.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            coupon.status === 'used' ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {coupon.status}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-gray-500 dark:text-gray-400">
                          {coupon.used_by || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default Coupon;
